import React, { FC, useCallback, useEffect, useState } from 'react';
import './AlgebraKit';
import { useUser } from '../../hooks';
import { Message } from '../forms';
import AlgebraKitSolution from './AlgebebraKitSolution';
import { UserBlockAnswers } from 'src/config/api/models/Answers';
import { StyleTeacherTitle, StyledContainer, TeacherSolutionContainer } from './Atoms';
import {
  useAddAlgebrakitScoreMutation,
  useGetAlgebrakitSessionIdQuery,
} from '../../slices/exercises/api/exercise';
import { CircularProgress } from '@material-ui/core';
import { useAppSelector } from '../../hooks/useAppDispatch';
import { StudentFiltersEnum, TeacherFiltersEnum } from '../../constants';
/**
 * Properties
 *
 * - session‑id: A session represents a single user solving a single exercise. You obtain the session id by calling Algebrakit’s web service from your backend, see create-session
 * - ref‑id: Refers to the particular math interaction or math resource within the exercise. You can find the ref-ids using the published-info endpoint.
 * - review‑mode: When set to 'true' the web components will show all student inputs and responses from Algebrakit. This feature is intended for reviewing the student's actions by a teacher.
 * - interactive‑mode: When set to 'false', students will not be able to make changes.
 *
 */

export type AlgebraKitExerciseProps = {
  sessionId?: string;
  startActive?: boolean;
  algebraKitExerciseId?: string;
  userAnswersFromPage?: UserBlockAnswers;
  shouldShow?: boolean;
};

const AlgebraKitExercise: FC<AlgebraKitExerciseProps> = ({
  algebraKitExerciseId,
  startActive = true,
  userAnswersFromPage,
  sessionId,
}) => {
  const $window: any = window;
  const solutionsMode = useAppSelector((state) => state.solutionsMode.solutionsMode);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const [answerGiven, setAnswerGiven] = useState(false);
  const { isStudent, isTeacher } = useUser();

  const [addScore, addScoreResponse] = useAddAlgebrakitScoreMutation();
  const {
    data: algebraKitsessionData,
    isFetching: algebraKitSessionIsFetching,
    isLoading: algebraKitSessionIsLoading,
    isError: algebraKitSessionIsError,
    isSuccess: algebraKitSessionIsSuccess,
  } = useGetAlgebrakitSessionIdQuery(algebraKitExerciseId as string, {
    skip: !algebraKitExerciseId,
  });

  const handleExerciseAttemptSubmit = useCallback(
    (exerciseId: string, sessionId: string) => {
      addScore({ exerciseId, sessionId });
    },
    [addScore]
  );

  useEffect(() => {
    setAnswerGiven(userAnswersFromPage !== undefined);
  }, [userAnswersFromPage]);

  useEffect(() => {
    if (addScoreResponse.isError) {
      console.log('error adding score', addScoreResponse.error);
    }
  }, [addScoreResponse]);

  useEffect(() => {
    if (
      algebraKitSessionIsSuccess &&
      $window.AlgebraKIT &&
      isStudent &&
      algebraKitsessionData.data.sessionId
    ) {
      $window.AlgebraKIT.addExerciseListener(
        algebraKitsessionData.data.sessionId,
        function (eventData: any) {
          switch (eventData.event) {
            case 'interaction-evaluate':
              handleExerciseAttemptSubmit(algebraKitExerciseId!, eventData.sessionId);
              setAnswerGiven(true);
              break;
          }
        }
      );
    }
  }, [
    algebraKitsessionData,
    handleExerciseAttemptSubmit,
    algebraKitExerciseId,
    $window.AlgebraKIT,
    algebraKitSessionIsSuccess,
    isStudent,
  ]);

  if (algebraKitSessionIsError) {
    return (
      <Message>
        We kunnen deze oefening met AlgebraKit niet tonen, omdat de oefening niet gekend is.
      </Message>
    );
  }

  if (algebraKitSessionIsFetching || algebraKitSessionIsLoading) {
    return <CircularProgress />;
  }

  return (
    <StyledContainer>
      {sessionId && (
        <akit-exercise
          session-id={sessionId}
          review-mode={
            activeFilter === StudentFiltersEnum.PROGRESS_AND_SCORES ||
            activeFilter === TeacherFiltersEnum.FOLLOW_UP
          }
        />
      )}

      {algebraKitSessionIsSuccess && algebraKitsessionData.data.sessionId && (
        <>
          {isTeacher && solutionsMode && (
            <TeacherSolutionContainer>
              <StyleTeacherTitle>Oplossing</StyleTeacherTitle>
              <akit-exercise
                session-id={algebraKitsessionData.data.sessionId}
                solution-mode={true}
              />
            </TeacherSolutionContainer>
          )}
          <akit-exercise
            session-id={algebraKitsessionData.data.sessionId}
            start-active={startActive}
          />

          {isStudent && (
            <AlgebraKitSolution activated={answerGiven}>
              <akit-exercise
                session-id={algebraKitsessionData.data.sessionId}
                solution-mode={true}
              />
            </AlgebraKitSolution>
          )}
        </>
      )}
    </StyledContainer>
  );
};

export default AlgebraKitExercise;
